import { createSlice } from "@reduxjs/toolkit";
import { apiCallBegan } from "./middleware/apiMiddleware";

const initialState = {
  templates: [],
  fetchTemplatesLoading: false,
  fetchTemplatesError: null,

  designs: [],
  fetchDesignsLoading: false,
  fetchDesignsError: null,

  currentDesign: null,
  fetchDesignByIdLoading: false,
  fetchDesignByIdError: null,

  saveDesignLoading: false,
  saveDesignError: null,
  lastSavedDesign: null,

  updateDesignLoading: false,
  updateDesignError: null,

  uploadImageLoading: false,
  uploadImageError: null,
  lastUploadedImage: null,

  builderImages: [],
  fetchBuilderImagesLoading: false,
  fetchBuilderImagesError: null,

  builderImageCategories: [],
  fetchBuilderImageCategoriesLoading: false,
  fetchBuilderImageCategoriesError: null,
};

const invitationSlice = createSlice({
  name: "invitation",
  initialState,
  reducers: {
    beginFetchTemplates: (state) => {
      state.fetchTemplatesLoading = true;
      state.fetchTemplatesError = null;
    },
    setTemplates: (state, action) => {
      state.fetchTemplatesLoading = false;
      state.templates = action.payload?.data ?? action.payload ?? [];
      state.fetchTemplatesError = null;
    },
    fetchTemplatesError: (state, action) => {
      state.fetchTemplatesLoading = false;
      state.fetchTemplatesError =
        action.payload?.errorMessage || "Failed to load templates";
    },

    beginFetchDesigns: (state) => {
      state.fetchDesignsLoading = true;
      state.fetchDesignsError = null;
    },
    setDesigns: (state, action) => {
      state.fetchDesignsLoading = false;
      state.designs = action.payload?.data ?? [];
      state.fetchDesignsError = null;
    },
    fetchDesignsError: (state, action) => {
      state.fetchDesignsLoading = false;
      state.fetchDesignsError =
        action.payload?.errorMessage || "Failed to load designs";
    },

    beginFetchDesignById: (state) => {
      state.fetchDesignByIdLoading = true;
      state.fetchDesignByIdError = null;
      state.currentDesign = null;
    },
    setCurrentDesign: (state, action) => {
      state.fetchDesignByIdLoading = false;
      state.currentDesign = action.payload?.data ?? action.payload ?? null;
      state.fetchDesignByIdError = null;
    },
    fetchDesignByIdError: (state, action) => {
      state.fetchDesignByIdLoading = false;
      state.fetchDesignByIdError =
        action.payload?.errorMessage || "Failed to load design";
    },
    clearCurrentDesign: (state) => {
      state.currentDesign = null;
    },

    beginSaveDesign: (state) => {
      state.saveDesignLoading = true;
      state.saveDesignError = null;
    },
    setSavedDesign: (state, action) => {
      state.saveDesignLoading = false;
      state.lastSavedDesign = action.payload?.data ?? action.payload;
      state.saveDesignError = null;
    },
    saveDesignError: (state, action) => {
      state.saveDesignLoading = false;
      state.saveDesignError =
        action.payload?.errorMessage || "Failed to save design";
    },

    beginUpdateDesign: (state) => {
      state.updateDesignLoading = true;
      state.updateDesignError = null;
    },
    setUpdatedDesign: (state, action) => {
      state.updateDesignLoading = false;
      const data = action.payload?.data ?? action.payload;
      state.lastSavedDesign = data;
      state.currentDesign = data;
      state.updateDesignError = null;
    },
    updateDesignError: (state, action) => {
      state.updateDesignLoading = false;
      state.updateDesignError =
        action.payload?.errorMessage || "Failed to update design";
    },

    beginUploadImage: (state) => {
      state.uploadImageLoading = true;
      state.uploadImageError = null;
    },
    setUploadedImage: (state, action) => {
      state.uploadImageLoading = false;
      state.lastUploadedImage = action.payload?.data ?? action.payload;
      state.uploadImageError = null;
    },
    uploadImageError: (state, action) => {
      state.uploadImageLoading = false;
      state.uploadImageError =
        action.payload?.errorMessage || "Failed to upload image";
    },

    beginFetchBuilderImages: (state) => {
      state.fetchBuilderImagesLoading = true;
      state.fetchBuilderImagesError = null;
    },
    setBuilderImages: (state, action) => {
      state.fetchBuilderImagesLoading = false;
      state.builderImages = action.payload?.data ?? action.payload ?? [];
      state.fetchBuilderImagesError = null;
    },
    fetchBuilderImagesError: (state, action) => {
      state.fetchBuilderImagesLoading = false;
      state.fetchBuilderImagesError =
        action.payload?.errorMessage || "Failed to load library images";
    },

    beginFetchBuilderImageCategories: (state) => {
      state.fetchBuilderImageCategoriesLoading = true;
      state.fetchBuilderImageCategoriesError = null;
    },
    setBuilderImageCategories: (state, action) => {
      state.fetchBuilderImageCategoriesLoading = false;
      state.builderImageCategories =
        action.payload?.data ?? action.payload ?? [];
      state.fetchBuilderImageCategoriesError = null;
    },
    fetchBuilderImageCategoriesError: (state, action) => {
      state.fetchBuilderImageCategoriesLoading = false;
      state.fetchBuilderImageCategoriesError =
        action.payload?.errorMessage || "Failed to load image categories";
    },
  },
});

export const {
  beginFetchTemplates,
  setTemplates,
  fetchTemplatesError,
  beginFetchDesigns,
  setDesigns,
  fetchDesignsError,
  beginFetchDesignById,
  setCurrentDesign,
  fetchDesignByIdError,
  clearCurrentDesign,
  beginSaveDesign,
  setSavedDesign,
  saveDesignError,
  beginUpdateDesign,
  setUpdatedDesign,
  updateDesignError,
  beginUploadImage,
  setUploadedImage,
  uploadImageError,
  beginFetchBuilderImages,
  setBuilderImages,
  fetchBuilderImagesError,
  beginFetchBuilderImageCategories,
  setBuilderImageCategories,
  fetchBuilderImageCategoriesError,
} = invitationSlice.actions;

export const fetchInvitationTemplates = (category) =>
  apiCallBegan({
    url: "/invitation-templates",
    method: "GET",
    params: category ? { category } : undefined,
    onStart: beginFetchTemplates.type,
    onSuccess: setTemplates.type,
    onError: fetchTemplatesError.type,
  });

export const fetchInvitationBuilderImages = (category) =>
  apiCallBegan({
    url: "/invitation-builder-images",
    method: "GET",
    params: category ? { category } : undefined,
    onStart: beginFetchBuilderImages.type,
    onSuccess: setBuilderImages.type,
    onError: fetchBuilderImagesError.type,
  });

export const fetchInvitationBuilderImageCategories = () =>
  apiCallBegan({
    url: "/invitation-builder-image-categories",
    method: "GET",
    onStart: beginFetchBuilderImageCategories.type,
    onSuccess: setBuilderImageCategories.type,
    onError: fetchBuilderImageCategoriesError.type,
  });

export const fetchInvitationDesigns = () =>
  apiCallBegan({
    url: "/invitation-designs",
    method: "GET",
    onStart: beginFetchDesigns.type,
    onSuccess: setDesigns.type,
    onError: fetchDesignsError.type,
  });

export const fetchInvitationDesignById = (id) =>
  apiCallBegan({
    url: `/invitation-designs/${id}`,
    method: "GET",
    onStart: beginFetchDesignById.type,
    onSuccess: setCurrentDesign.type,
    onError: fetchDesignByIdError.type,
  });

export const createInvitationDesign = (body) =>
  apiCallBegan({
    url: "/invitation-designs",
    method: "POST",
    data: body,
    onStart: beginSaveDesign.type,
    onSuccess: setSavedDesign.type,
    onError: saveDesignError.type,
  });

export const updateInvitationDesign = (id, body) =>
  apiCallBegan({
    url: `/invitation-designs/${id}`,
    method: "PUT",
    data: body,
    onStart: beginUpdateDesign.type,
    onSuccess: setUpdatedDesign.type,
    onError: updateDesignError.type,
  });

export const uploadInvitationBuilderImage = (fileBase64, fileExtension = "png") =>
  apiCallBegan({
    url: "/invitation-builder/upload-image",
    method: "POST",
    data: { file: fileBase64, file_extension: fileExtension },
    onStart: beginUploadImage.type,
    onSuccess: setUploadedImage.type,
    onError: uploadImageError.type,
  });

export default invitationSlice.reducer;
